/*
- Key behaviors:
- It is meant to be used with Json requests and responses
- JSON.Stringify the body of the request
- JSON.Stringify the response
- return consistent type incase of error and success
*/
export interface FetchJsonReturnType<T> {
  response: Response | undefined;
  data: T;
  error: any;
  status: "not-started" | "success" | "error";
  aborted: boolean;
}

export interface FetchRequestConfig extends RequestInit {
  body?: any;
}

export async function fetchJson<T>(
  request: RequestInfo,
  config: FetchRequestConfig = {}
): Promise<FetchJsonReturnType<T>> {
  const finalConfig = {
    method: config.body ? "POST" : "GET",
    ...config,
    body: config.body ? JSON.stringify(config.body) : undefined,
    headers: {
      "content-type": "application/json",
      ...config.headers,
    },
  };

  try {
    const response = await window.fetch(request, finalConfig);
    const body = await tryReadBody(response);
    if (response.ok) {
      return Promise.resolve({
        response,
        data: body,
        error: undefined,
        status: "success",
        aborted: false,
      });
    } else {
      return Promise.reject(
        new FetchJsonError({
          response,
          data: undefined,
          error: body,
          status: "error",
          aborted: false,
        })
      );
    }
  } catch (error: any) {
    return Promise.reject(
      new FetchJsonError({
        error,
        status: "error",
        aborted: error?.name === "AbortError",
      })
    );
  }
}

async function tryReadBody(response: Response): Promise<any> {
  try {
    const textResponse = await response.text();
    const json = JSON.parse(textResponse);
    return json;
  } catch (error) {
    return undefined;
  }
}

interface FetchJsonErrorCtorType<T> extends Partial<FetchJsonReturnType<T>> {}
export class FetchJsonError extends Error {
  data: any;
  error: any;
  status: FetchJsonReturnType<any>["status"];
  response?: Response;
  aborted?: boolean;

  constructor({ data, error, response, aborted }: FetchJsonErrorCtorType<any>) {
    super("FetchJsonError");
    this.name = "FetchJsonError";

    this.data = data;
    this.error = error;
    this.status = "error";
    this.response = response;
    this.aborted = aborted;
  }
}
